/**
 * Static `select` primitive for the Monitor wizard.
 *
 * Renders a shadcn Select bound to the field's `options` array. Used for
 * single-choice fields whose options are declared inline on the capability
 * (e.g. Intercom's `channel` and `initial_lookback` — both date-window-style
 * presets baked into the capability schema rather than fetched dynamically).
 *
 * Async option providers (script-backed or backend-tool) use MultiSelectField
 * or a future AsyncSelectField — NOT this component. A `type: select` field
 * with `options_provider` set will render an empty dropdown here; the spec's
 * primitive contract reserves the `async_select` type for that case, and
 * FieldRenderer's fallback surfaces it as "Unsupported field type" until
 * the AsyncSelectField primitive lands.
 */
import React from 'react';

import { Label } from '../../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/select';
import type { CapabilityField } from '../../../../../types/importCapabilities';

interface SelectFieldProps {
  field: CapabilityField;
  value: string | undefined;
  onChange: (value: string) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({ field, value, onChange }) => {
  const staticOptions = field.options ?? [];
  const labelId = `field-${field.id}-label`;

  return (
    <div className="space-y-1.5">
      <Label id={labelId} htmlFor={`field-${field.id}`}>
        {field.label || field.id}
        {field.required ? <span className="ml-1 text-red-500">*</span> : null}
      </Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger id={`field-${field.id}`} aria-labelledby={labelId}>
          <SelectValue placeholder={`Pick a ${field.label || field.id}…`} />
        </SelectTrigger>
        <SelectContent>
          {staticOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {field.description ? (
        <p className="text-xs text-slate-500">{field.description}</p>
      ) : null}
    </div>
  );
};

export default SelectField;
